import React, {PureComponent} from "react";
import {connect} from "react-redux";
import styles from "./tutorial.module.css";
import {cssHelper} from "../../../../utils/cssHelper";
import icons from "../../../../styles/icons.module.css";
import {ITutorialRequest} from "../../../../../store/tutorial/types";
import {TweenLite, TimelineLite, Animation} from "gsap";
import {ICanvasDimensions} from "../../../../../store/settings/types";
import getRelativePosition from "../../../../utils/getRelativePosition";
import getFontSize from "../../../../utils/getFontSize";
import {IApplicationState} from "../../../../../store";
import {DialogManagerProps} from "../../../../../store/dialog-manager/types";
import {iif} from "../../../iif";
import renderText from "../../../renderText";
import animations from "../../../../styles/animations.module.css";
import i18n from "../../../../i18n";

interface TutorialProps extends ITutorialRequest {
    delay: number;
    canvas: ICanvasDimensions;
}

class Tutorial extends PureComponent<TutorialProps & DialogManagerProps> {
    private readonly hostElement: React.RefObject<HTMLDivElement>;
    private readonly target?: HTMLElement;
    private readonly targetParent?: HTMLElement;
    private readonly tweenDuration = 0.33;
    private readonly onTouch: (e: Event) => void;
    private activeTween?: Animation;
    private shownTimestamp = 0;
    private targetDimensions: Record<string, string | null> = {
        width: null,
        left: null,
        top: null,
        marginLeft: null,
    };

    constructor(props: TutorialProps & DialogManagerProps, context: any) {
        super(props, context);

        this.onTouch = this.close.bind(this);
        this.hostElement = React.createRef();

        if (this.props.selector) {
            this.target = document.querySelector(this.props.selector) as HTMLElement;

            if (this.target) {
                this.targetParent = this.target.parentElement as HTMLElement;
            }
        }
    }

    render() {
        const {title} = this.props;

        const localizedText = title ? i18n(title) : undefined;
        const minFontSize = this.props.selector ? 28 : 36;
        const fontSize = localizedText
            ? getFontSize(localizedText.length, minFontSize, 40, 30, 70)
            : 40;

        return (
            <div className={styles.container} ref={this.hostElement} onClick={(e) => this.props.closeOnTouch ? this.close(e) : undefined}>
                <div className={styles.overlay}/>

                <button className={cssHelper(styles.close_button, icons.close).toString()} onClick={(e) => this.close(e)}>✖</button>

                <div className={styles.text} style={{ fontSize }}>
                    {localizedText}

                    {iif(!this.props.selector,
                        renderText(i18n("dialog.taptocontinue"), 26, 32, 15, 30, cssHelper(styles.tap_to_continue, animations.blink).toString())
                    )}

                    <i className={styles.arrow}/>
                </div>
            </div>
        );
    }

    componentDidMount(): void {
        this.props.onShow && this.props.onShow();

        this.shownTimestamp = new Date().getTime();

        if (!this.hostElement.current)
            return;

        const {delay, canvas, animation} = this.props;

        const overlay = this.hostElement.current.getElementsByClassName(styles.overlay)[0] as HTMLElement;
        const text = this.hostElement.current.getElementsByClassName(styles.text)[0] as HTMLElement;
        const closeButton = this.hostElement.current.getElementsByClassName(styles.close_button)[0] as HTMLElement;

        if (animation.enabled) {
            this.activeTween = new TimelineLite()
                .fromTo(overlay, this.tweenDuration, {
                    opacity: 0,
                }, {
                    opacity: 1,
                    delay: delay,
                })
                .fromTo(text, this.tweenDuration, {
                    opacity: 0,
                }, {
                    opacity: 1,
                    delay: -this.tweenDuration / 2
                })
                .fromTo(closeButton, this.tweenDuration, {
                    opacity: 0,
                }, {
                    opacity: 1,
                    delay: -this.tweenDuration / 2
                })
                .timeScale(animation.timeScale);
        } else {
            TweenLite.set(overlay, {
                opacity: 1,
            });
        }

        setTimeout(() => {
            if (!this.target || !this.targetParent || !this.hostElement.current) {
                text.classList.add(styles.text_center);
                return;
            }

            this.target.removeEventListener('click', this.onTouch, true);
            this.target.addEventListener('click', this.onTouch, true);

            const position = getRelativePosition(this.target, this.hostElement.current);

            this.targetDimensions = {
                width: this.target.style.width,
                left: this.target.style.left,
                top: this.target.style.top,
                marginLeft: this.target.style.marginLeft,
            };

            const targetWidth = this.target.offsetWidth;

            this.targetParent.removeChild(this.target);
            this.hostElement.current.appendChild(this.target);

            this.target.style.width = targetWidth + "px";
            this.target.style.left = position.left + "px";
            this.target.style.top = position.top + "px";
            this.target.style.marginLeft = "0px";

            if (targetWidth < canvas.width * 0.65) {
                if (position.left < canvas.width / 2) {
                    position.left += targetWidth;
                    text.classList.add(styles.text_right);
                } else {
                    position.left -= text.offsetWidth;
                    text.classList.add(styles.text_left);
                }
            } else {
                position.left += targetWidth / 2 - text.offsetWidth / 3;
            }

            text.style.width = Math.max(150, Math.min(300, canvas.width - position.left - 15)) + "px";

            if (position.top < canvas.height / 2) {
                position.top += this.target.offsetHeight;
                text.classList.add(styles.text_bottom);
            } else {
                position.top -= text.offsetHeight;
                text.classList.add(styles.text_top);
            }

            text.style.left = position.left + "px";
            text.style.top = position.top + "px";
        }, animation.enabled ? (delay - this.tweenDuration / 2) * 1000 / animation.timeScale : 0);
    }

    componentWillUnmount(): void {
        this.activeTween && this.activeTween.kill();

        this.restoreTarget();
    }

    private close(event: any) {
        if (this.props.closeOnTouch && !this.props.propagateEvents) {
            event && event.stopPropagation();
        }

        if (new Date().getTime() < this.shownTimestamp + (this.props.delay + this.tweenDuration) * 1000) {
            return;
        }

        this.restoreTarget();

        this.props.close();
    }

    private restoreTarget() {
        if (this.target && this.targetParent && this.hostElement.current && this.target.parentElement === this.hostElement.current) {
            this.hostElement.current.removeChild(this.target);
            this.targetParent.appendChild(this.target);

            this.target.style.width = this.targetDimensions.width || '';
            this.target.style.left = this.targetDimensions.left || '';
            this.target.style.top = this.targetDimensions.top || '';
            this.target.style.marginLeft = this.targetDimensions.marginLeft || '';

            this.target.removeEventListener('click', this.onTouch, true);
        }
    }
}

export default connect(({ tutorial, settings }: IApplicationState) => ({
    tutorials: tutorial.tutorials,
    canvas: settings.canvas,
    animation: settings.animation,
}))(Tutorial);